import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

interface PasswordFormProps {
  saving: boolean;
  onChangePassword: (current: string, next: string) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

export default function PasswordForm({ saving, onChangePassword, notify }: PasswordFormProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!current) {
      setError(t("settings.errCurrent"));
      return;
    }
    if (next.length < 6) {
      setError(t("settings.errMin"));
      return;
    }
    if (next !== confirm) {
      setError(t("settings.errMismatch"));
      return;
    }
    if (next === current) {
      setError(t("settings.errSame"));
      return;
    }
    setError(null);
    try {
      await onChangePassword(current, next);
      reset();
      notify(t("settings.passwordUpdated"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("settings.errFailed"), "error");
    }
  };

  const inputType = show ? "text" : "password";

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("settings.passwordTitle")}</h2>
        <button
          type="button"
          onClick={() => setShow((value) => !value)}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className={show ? "ri-eye-off-line" : "ri-eye-line"} />
          </span>
          {show ? t("settings.hidePassword") : t("settings.showPassword")}
        </button>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
          {t("settings.currentPassword")}
          <input
            type={inputType}
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            placeholder={t("settings.currentPasswordPlaceholder")}
            autoComplete="current-password"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
            {t("settings.newPassword")}
            <input
              type={inputType}
              value={next}
              onChange={(event) => setNext(event.target.value)}
              placeholder={t("settings.newPasswordPlaceholder")}
              autoComplete="new-password"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
            {t("settings.confirmPassword")}
            <input
              type={inputType}
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              placeholder={t("settings.confirmPasswordPlaceholder")}
              autoComplete="new-password"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </label>
        </div>
      </div>

      {error && <p className="mt-3 text-xs text-accent-700">{error}</p>}

      <button
        type="submit"
        disabled={saving}
        className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? t("settings.updatingPassword") : t("settings.updatePassword")}
      </button>
    </form>
  );
}